'use client';

import { useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useTranslations } from 'next-intl';
import { MdErrorOutline } from 'react-icons/md';

import { BottomSheet } from '@/components/ui/bottom-sheet';
import { Button, ButtonSize } from '@/components/ui/button';
import { useFocusTrap } from '@/hooks/useFocusTrap';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import {
  failedDialog,
  failedMessage,
  failedOverlay,
  failedSheetBody,
  failedTitle,
} from './CaptchaVerificationFailed.style';
import type { CaptchaVerificationFailedProps } from './CaptchaVerificationFailed.types';

const DESKTOP_QUERY = '(min-width: 768px)';

/** `BottomSheet.onClose` is required by its props but unreachable while `dismissible` is false. */
const NO_DISMISS = () => {};

/**
 * The end of the road: verification could not be completed and the page has to be reloaded.
 *
 * Shown after a request has spent its whole challenge budget. Replaces the puzzle rather than
 * stacking on it — see `CaptchaChallengeProvider`.
 *
 * ## Invariants:
 * - Reload, never a router navigation. Why: the point of the reload is to discard the page's
 *   entire client state — the stale reCAPTCHA script instance included, which is what the retries
 *   kept failing against. A `router.push` of the same route keeps all of it and re-runs the
 *   customer straight back into the same refusal.
 * - Exactly one control, and no way out but that control. Why: everything else on this page is
 *   now unusable — every guarded request behind this popup has already failed three times — so
 *   offering a dismissal would hand the customer back a screen that cannot do anything, with no
 *   explanation of why. Both branches are non-dismissible: no close control, and backdrop click
 *   and Escape do nothing.
 * - Says nothing about tokens, scores or reCAPTCHA. Why: it is the only copy the customer sees
 *   for this failure and none of that vocabulary tells them what to do about it.
 * - Branches on `useMediaQuery`, never CSS visibility. Why: both branches portal to
 *   `document.body`, so a `tw:hidden` wrapper does not contain them and BOTH would mount at once
 *   — the portal landmine in this repo's CLAUDE.md.
 */
export const CaptchaVerificationFailed = ({ open }: CaptchaVerificationFailedProps) => {
  const t = useTranslations('Captcha');
  const isDesktop = useMediaQuery(DESKTOP_QUERY);
  const dialogRef = useRef<HTMLDivElement>(null);

  const handleRefresh = useCallback(() => {
    window.location.reload();
  }, []);

  // Keyboard focus stays inside the dialog; the desktop overlay covers the page, so nothing
  // behind it is reachable by pointer either. No Escape listener by design.
  useFocusTrap(dialogRef, open && isDesktop);

  if (!open) return null;

  const refreshButton = (
    <Button size={ButtonSize.large} onClick={handleRefresh} data-cy="captcha-failed-refresh">
      {t('refreshPage')}
    </Button>
  );

  const message = (
    <p className={failedMessage} data-cy="captcha-failed-message">
      {t('verificationFailedMessage')}
    </p>
  );

  if (isDesktop) {
    return createPortal(
      <div className={failedOverlay} data-cy="captcha-failed-overlay">
        <div
          ref={dialogRef}
          role="alertdialog"
          aria-modal="true"
          aria-label={t('verificationFailedTitle')}
          className={failedDialog}
          data-cy="captcha-failed-dialog"
        >
          <MdErrorOutline size={40} className="tw:text-error" aria-hidden="true" />
          <span className={failedTitle} data-cy="captcha-failed-title">
            {t('verificationFailedTitle')}
          </span>
          {message}
          {refreshButton}
        </div>
      </div>,
      document.body
    );
  }

  return (
    <BottomSheet
      open={open}
      onClose={NO_DISMISS}
      dismissible={false}
      title={t('verificationFailedTitle')}
      bodyClassName={failedSheetBody}
      data-cy="captcha-failed-sheet"
    >
      {message}
      {refreshButton}
    </BottomSheet>
  );
};

CaptchaVerificationFailed.displayName = 'CaptchaVerificationFailed';
